import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { 
  Sun, Moon, Menu, X, ChevronDown, 
  FileText, FilePlus, Layers, FileImage, 
  FileDown, FileSearch, Scissors, 
  Type, QrCode, Youtube, 
  Instagram, Settings, MessageSquare,
  Grid2X2, ShieldCheck, Zap
} from 'lucide-react';
import BrandLogoMark from '../brand/BrandLogoMark.jsx';
import { useDarkMode } from '../../hooks/useDarkMode.js';
import MegaMenu from './MegaMenu.jsx';

export default function Header() {
  const { isDark, toggle } = useDarkMode();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null); // 'convert' | 'all'
  const [scrolled, setScrolled] = useState(false);
  const timeoutRef = useRef(null);

  const handleEnter = (menu) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenMega(menu);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenMega(null), 200);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const convertSections = [
    {
      title: 'Convert to PDF',
      items: [
        { label: 'JPG to PDF', to: '/image-to-pdf', icon: FileImage, iconColor: 'text-orange-500', iconBg: 'bg-orange-50' },
        { label: 'Word to PDF', to: '/word-to-pdf', icon: FileText, iconColor: 'text-blue-500', iconBg: 'bg-blue-50' },
        { label: 'Excel to PDF', to: '/csv-excel-converter', icon: Grid2X2, iconColor: 'text-green-500', iconBg: 'bg-green-50' },
        { label: 'PPT to PDF', to: '/universal-converter', icon: FileDown, iconColor: 'text-red-500', iconBg: 'bg-red-50' },
        { label: 'HTML to PDF', to: '/universal-converter', icon: FilePlus, iconColor: 'text-yellow-500', iconBg: 'bg-yellow-50' },
      ]
    },
    {
      title: 'Convert from PDF',
      items: [
        { label: 'PDF to JPG', to: '/pdf-to-jpg', icon: FileImage, iconColor: 'text-orange-600', iconBg: 'bg-orange-100/50' },
        { label: 'PDF to Word', to: '/pdf-to-word', icon: FileText, iconColor: 'text-blue-600', iconBg: 'bg-blue-100/50' },
        { label: 'PDF to Excel', to: '/csv-excel-converter', icon: Grid2X2, iconColor: 'text-green-600', iconBg: 'bg-green-100/50' },
        { label: 'PDF to PPT', to: '/universal-converter', icon: FileSearch, iconColor: 'text-red-600', iconBg: 'bg-red-100/50' },
        { label: 'PDF to PDF/A', to: '/universal-converter', icon: ShieldCheck, iconColor: 'text-slate-600', iconBg: 'bg-slate-100' },
      ]
    }
  ];

  const allToolsSections = [
    {
      title: 'Optimize PDF',
      items: [
        { label: 'Merge PDF', to: '/merge-pdf', icon: Layers, iconColor: 'text-indigo-500', iconBg: 'bg-indigo-50' },
        { label: 'Split PDF', to: '/split-pdf', icon: Scissors, iconColor: 'text-purple-500', iconBg: 'bg-purple-50' },
        { label: 'Compress PDF', to: '/image-compressor', icon: Zap, iconColor: 'text-yellow-600', iconBg: 'bg-yellow-50' },
        { label: 'Repair PDF', to: '/universal-converter', icon: Settings, iconColor: 'text-slate-500', iconBg: 'bg-slate-100' },
      ]
    },
    {
      title: 'Other Utilities',
      items: [
        { label: 'QR Generator', to: '/qr-code-generator', icon: QrCode, iconColor: 'text-brand-600', iconBg: 'bg-brand-50' },
        { label: 'QR Reader', to: '/qr-code-reader', icon: FileSearch, iconColor: 'text-brand-700', iconBg: 'bg-brand-100/30' },
        { label: 'YouTube Downloader', to: '/youtube-downloader', icon: Youtube, iconColor: 'text-red-600', iconBg: 'bg-red-50' },
        { label: 'Instagram Save', to: '/instagram-downloader', icon: Instagram, iconColor: 'text-pink-600', iconBg: 'bg-pink-50' },
        { label: 'Image to Text', to: '/image-to-text', icon: Type, iconColor: 'text-sky-600', iconBg: 'bg-sky-50' },
      ]
    }
  ];

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-800/50 shadow-sm py-0' 
        : 'bg-transparent border-b border-transparent py-2'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <BrandLogoMark size={32} variant="onBrand" className="shrink-0 transition-transform group-hover:scale-110" />
          <span className="font-display text-2xl font-bold text-slate-900 dark:text-white">FreeTooly</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8 h-full">
          <NavLink to="/merge-pdf" className={({ isActive }) => `text-sm font-bold uppercase tracking-tight transition-colors py-2 ${isActive ? 'text-brand-600 border-b-2 border-brand-600' : 'text-slate-700 dark:text-slate-300 hover:text-brand-600'}`}>
            Merge PDF
          </NavLink>
          <NavLink to="/split-pdf" className={({ isActive }) => `text-sm font-bold uppercase tracking-tight transition-colors py-2 ${isActive ? 'text-brand-600 border-b-2 border-brand-600' : 'text-slate-700 dark:text-slate-300 hover:text-brand-600'}`}>
            Split PDF
          </NavLink>
          <NavLink to="/image-compressor" className={({ isActive }) => `text-sm font-bold uppercase tracking-tight transition-colors py-2 ${isActive ? 'text-brand-600 border-b-2 border-brand-600' : 'text-slate-700 dark:text-slate-300 hover:text-brand-600'}`}>
            Compress PDF
          </NavLink>
          <div 
            className="relative h-full flex items-center"
            onMouseEnter={() => handleEnter('convert')}
            onMouseLeave={handleLeave}
          >
            <button 
              className={`flex items-center gap-1 text-sm font-bold uppercase tracking-tight transition-colors py-2 ${openMega === 'convert' ? 'text-brand-600' : 'text-slate-700 dark:text-slate-300 hover:text-brand-600'}`}
              aria-expanded={openMega === 'convert'}
              aria-haspopup="true"
              aria-controls="mega-menu-convert"
            >
              Convert PDF <ChevronDown size={14} className={`transition-transform duration-200 ${openMega === 'convert' ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            <div id="mega-menu-convert">
              <MegaMenu sections={convertSections} isOpen={openMega === 'convert'} />
            </div>
          </div>

          <div 
            className="relative h-full flex items-center"
            onMouseEnter={() => handleEnter('all')}
            onMouseLeave={handleLeave}
          >
            <button 
              className={`flex items-center gap-1 text-sm font-bold uppercase tracking-tight transition-colors py-2 ${openMega === 'all' ? 'text-brand-600' : 'text-slate-700 dark:text-slate-300 hover:text-brand-600'}`}
              aria-expanded={openMega === 'all'}
              aria-haspopup="true"
              aria-controls="mega-menu-all"
            >
              All PDF Tools <ChevronDown size={14} className={`transition-transform duration-200 ${openMega === 'all' ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            <div id="mega-menu-all">
              <MegaMenu sections={allToolsSections} isOpen={openMega === 'all'} />
            </div>
          </div>
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            className="interactive-tap w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 dark:text-slate-400 hover:bg-sky-100/80 dark:hover:bg-slate-800 transition-all font-bold"
          >
            {isDark ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
          </button>
          
          <Link
            to="/contact"
            className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wide text-brand-600 bg-brand-50 dark:bg-brand-900/10 border border-brand-100 dark:border-brand-900/30 hover:bg-brand-100 transition-all shrink-0"
            aria-label="Contact us for help and support"
          >
            <MessageSquare size={14} aria-hidden="true" />
            Contact Us
          </Link>
          
          {/* Mobile menu toggle */}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? "Close main menu" : "Open main menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            className="interactive-tap lg:hidden w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 dark:text-slate-400 hover:bg-sky-100/80 dark:hover:bg-slate-800 transition-all"
          >
            {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-sky-200/50 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur-md px-4 py-3 flex flex-col gap-1 overflow-y-auto max-h-[80vh]">
          <div className="py-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest px-3">Quick Tools</div>
          <NavLink to="/merge-pdf" onClick={() => setMenuOpen(false)} className="px-3 py-2.5 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-sky-50 dark:hover:bg-slate-800 transition-all">Merge PDF</NavLink>
          <NavLink to="/image-compressor" onClick={() => setMenuOpen(false)} className="px-3 py-2.5 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-sky-50 dark:hover:bg-slate-800 transition-all">Compress PDF</NavLink>
          
          <div className="mt-4 py-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest px-3">Convert</div>
          {convertSections[0].items.map(item => (
            <Link key={item.to} to={item.to} onClick={() => setMenuOpen(false)} className="px-3 py-2.5 rounded-xl text-sm text-slate-600 dark:text-slate-400 hover:bg-sky-50 dark:hover:bg-slate-800 transition-all">{item.label}</Link>
          ))}
          {convertSections[1].items.map(item => (
            <Link key={item.to} to={item.to} onClick={() => setMenuOpen(false)} className="px-3 py-2.5 rounded-xl text-sm text-slate-600 dark:text-slate-400 hover:bg-sky-50 dark:hover:bg-slate-800 transition-all">{item.label}</Link>
          ))}
          <Link 
            to="/contact" 
            onClick={() => setMenuOpen(false)} 
            className="flex items-center gap-2 mx-3 my-2 px-4 py-3 rounded-2xl text-sm font-bold text-slate-800 dark:text-white bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:bg-slate-100 transition-all shadow-sm"
          >
            <MessageSquare size={16} className="text-brand-500" />
            Help & Support
          </Link>
        </div>
      )}
    </header>
  );
}
